import React, { createContext, useContext, useEffect, useState } from 'react';
import api, { tokenManager } from '../services/api';
import config from '../config/api';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);

    // Helper to check user roles
    const hasRole = (role) => {
        return user?.roles?.includes(role) || false;
    };

    const isSuperAdmin = () => hasRole('SuperAdmin');
    const isTenantOwner = () => hasRole('TenantOwner');
    const isTenantUser = () => hasRole('TenantUser');

    const location = useLocation();

    useEffect(() => {
        const initAuth = async () => {
            const publicRoutes = [
                '/login',
                '/signup',
                '/accept-invite',
                '/forgot-password',
                '/reset-password',
                '/verify-email',
                '/2fa',
            ];

            const isPublicRoute = publicRoutes.some(route =>
                location.pathname.startsWith(route)
            );

            if (isPublicRoute) {
                setLoading(false);
                return;
            }

            const token = tokenManager.getAccessToken();
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                const response = await api.get(config.endpoints.user.me);
                if (response.success) {
                    setUser(response.data.user);
                }
            } catch {
                tokenManager.clearTokens();
                setUser(null);
                setTenant(null);
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, [location.pathname]);   // ✅ THIS FIXES IT

    const fetchCurrentTenant = async () => {
        try {
            const response = await api.get(config.endpoints.tenant.current);
            if (response.success && response.data) {
                setTenant(response.data);
                tokenManager.setTenantId(response.data.id);
            }
        } catch (error) {
            console.error('Failed to fetch tenant:', error);
        }
    };

    // Register Tenant (Tenant Owner Registration)
    const registerTenant = async (tenantName, tenantSlug, email, password, firstName, lastName) => {
        try {
            const response = await api.post(config.endpoints.auth.registerTenant, {
                tenantName,
                tenantSlug,
                email,
                password,
                firstName,
                lastName,
            });

            if (response.success) {
                const { user: userData, tenant: tenantData, accessToken, refreshToken } = response.data;
                tokenManager.setAccessToken(accessToken);
                tokenManager.setRefreshToken(refreshToken);
                tokenManager.setTenantId(tenantData.id);
                setUser(userData);
                setTenant(tenantData);
                return { success: true, data: response.data };
            }
        } catch (error) {
            return { success: false, error: error.message || 'Registration failed' };
        }
    };

    // Accept Invite (User Registration via Invite)
    const acceptInvite = async (token, password, firstName, lastName) => {
        try {
            const response = await api.post(config.endpoints.auth.acceptInvite, {
                token,
                password,
                firstName,
                lastName,
            });

            if (response.success) {
                const { user: userData, tokens } = response.data;
                tokenManager.setAccessToken(tokens.accessToken);
                tokenManager.setRefreshToken(tokens.refreshToken);
                setUser(userData);

                // Extract tenant from user data or fetch separately
                if (userData.tenant) {
                    setTenant(userData.tenant);
                    tokenManager.setTenantId(userData.tenant.id);
                } else {
                    await fetchCurrentTenant();
                }

                return { success: true, data: response.data };
            }
        } catch (error) {
            return { success: false, error: error.message || 'Failed to accept invite' };
        }
    };

    // Login
    const signIn = async (email, password) => {
        try {
            const response = await api.post(config.endpoints.auth.login, {
                email,
                password,
            });

            console.log('Login response:', response);

            if (response.success) {
                const { user: userData, accessToken, refreshToken } = response.data;
                tokenManager.setAccessToken(accessToken);
                tokenManager.setRefreshToken(refreshToken);
                setUser(userData);
                toast.success('Login successful!');

                // Extract tenant from user data if available
                // if (userData.tenant) {
                //     setTenant(userData.tenant);
                //     tokenManager.setTenantId(userData.tenant.id);
                // } else if (!userData.roles.includes('SuperAdmin')) {
                //     // If not Super Admin and no tenant embedded, fetch separately
                //     await fetchCurrentTenant();
                // }

                return { success: true, data: response.data };
            } else {
                console.log("Login failed response: ", response);
                toast.error(response.message || 'Login failed. Please try again.');
                return { success: false, error: response.message || 'Login failed' };
            }
        } catch (error) {
            console.log("Login Failed Response: ", error);

            return { success: false, error: error.message || 'Login failed' };
        }
    };

    // Refresh Token
    const refreshAccessToken = async () => {
        try {
            const refreshToken = tokenManager.getRefreshToken();
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }

            const response = await api.post(config.endpoints.auth.refresh, {
                refreshToken,
            });

            if (response.success) {
                tokenManager.setAccessToken(response.data.accessToken);
                return { success: true };
            }
        } catch (error) {
            tokenManager.clearTokens();
            setUser(null);
            setTenant(null);
            return { success: false, error: error.message };
        }
    };

    // Logout
    const signOut = async () => {
        try {
            await api.post(config.endpoints.auth.logout, {
                refreshToken: tokenManager.getRefreshToken(),
            });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            tokenManager.clearTokens();
            setUser(null);
            setTenant(null);
        }
    };

    // Update Profile
    const updateProfile = async (userId, data) => {
        try {
            const response = await api.patch(config.endpoints.admin.updateUser(userId), data);
            if (response.success) {
                return { success: true, data: response.data.user };
            } else {
                return { success: false, error: response.message || 'Failed to update profile' };
            }
        } catch (error) {
            return { success: false, error: error.message || 'Failed to update profile' };
        }
    };

    // Update Password
    const updatePassword = async (currentPassword, newPassword) => {
        try {
            const response = await api.patch(config.endpoints.user.updatePassword, {
                currentPassword,
                newPassword,
            });
            return { success: response.success, message: response.message };
        } catch (error) {
            return { success: false, error: error.message || 'Failed to update password' };
        }
    };

    const value = {
        user,
        tenant,
        loading,
        // Role checks
        hasRole,
        isSuperAdmin,
        isTenantOwner,
        isTenantUser,
        // Auth methods
        registerTenant,
        acceptInvite,
        signIn,
        signOut,
        refreshAccessToken,
        updateProfile,
        updatePassword,
        fetchCurrentTenant,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};